/** The Analytics map: one level's features (links as lines; hexagons, city hexagons and
 *  tracts as areas) coloured by one metric at one hour. A click selects a feature, a
 *  click on empty map clears it. Read-only.
 *
 *  Areas are coloured by a Mapbox fill layer slotted under the basemap's labels, so street
 *  and place names stay readable on top of them; deck.gl draws the same areas invisibly
 *  above for hover, click and the selection outline. Links are deck.gl lines as before.
 *
 *  Every level covers the Bay Area, so the map opens on the region and keeps the reader's
 *  pan and zoom when the level changes. */

import { useEffect, useMemo, useRef, useState } from 'react';
import { DeckGL } from '@deck.gl/react';
import { WebMercatorViewport } from '@deck.gl/core';
import { Map, Source, Layer } from 'react-map-gl';
import { GeoJsonLayer, PathLayer } from '@deck.gl/layers';
import { useReleaseWebGL } from './webgl';
import { fmtValue, linkValue } from './analytics';
import { REGION_VIEW } from './WorkshopMap';

// The view is reported once the map has stopped moving for this long (the V/C map waits
// 350 ms; measured 2026-10-28, 150 ms saves 0.2 s per pan without extra requests).
const VIEW_SETTLE_MS = 150;

// The Bay Area view, centred a little west of the region's so that San Francisco clears the
// control panel on the left of the map.
const HEX_VIEW = { ...REGION_VIEW, longitude: -122.55 };
const NO_DATA = [140, 140, 140, 60];
const PICKED = [255, 255, 255, 255];
const ROAD_WIDTH = { motorway: 3.5, motorway_link: 2, trunk: 3, trunk_link: 2, primary: 2.2, secondary: 1.6 };
// Under the labels the fill can be stronger and the streets still show; the page's slider
// sets it (`fill`, 0-1). No-data areas stay a faint grey whatever it is.
const NO_DATA_OPACITY = 0.12;
// Picking needs something drawn; nearly transparent keeps it off the colours below.
const INVISIBLE = [0, 0, 0, 1];

/** A GeoJSON geometry for an area feature: tracts carry one, hexagons a ring. */
function geometryOf(f) {
  if (f.geometry) return f.geometry;
  const ring = f.polygon;
  const [x0, y0] = ring[0];
  const [x1, y1] = ring[ring.length - 1];
  return { type: 'Polygon', coordinates: [x0 === x1 && y0 === y1 ? ring : [...ring, ring[0]]] };
}

/** `viewLinks`: the roads in view from the database (/api/traffic/links), drawn instead of
 *  the static links when present. `onViewChange({ bounds, zoom })`: when the map opens and
 *  whenever it has settled after a move. */
export default function AnalyticsMap({
  level, layer, metric, hour, colorOf, selected, onSelect, fill = 0.4, legend, children,
  viewLinks = null, onViewChange = null,
}) {
  const host = useRef(null);
  useReleaseWebGL(host);
  const [labelsFrom, setLabelsFrom] = useState(undefined);
  const h = metric.hourly === false ? 0 : hour;
  const isArea = level !== 'link';

  const settle = useRef(null);
  const tellView = useRef(null);
  tellView.current = (viewState) => {
    const el = host.current;
    if (!onViewChange || !el || !el.clientWidth || !el.clientHeight) return;
    const [w, s, e, n] = new WebMercatorViewport({ ...viewState, width: el.clientWidth, height: el.clientHeight }).getBounds();
    onViewChange({ bounds: [w, s, e, n], zoom: viewState.zoom });
  };
  useEffect(() => {
    tellView.current(HEX_VIEW);
    return () => clearTimeout(settle.current);
  }, []);
  const onViewStateChange = ({ viewState }) => {
    clearTimeout(settle.current);
    settle.current = setTimeout(() => tellView.current(viewState), VIEW_SETTLE_MS);
  };
  // The static link a database road stands for (selection, the side panels): by edge id.
  // globalThis.Map: `Map` here is react-map-gl's component.
  const linkIndex = useMemo(() => (level === 'link' ? new globalThis.Map(layer.features.map((f, i) => [f.id, i])) : null), [layer, level]);
  const selectedId = level === 'link' && selected != null ? layer.features[selected]?.id : null;

  const value = (i) => {
    const v = layer.values(i, h);
    return v == null || !Number.isFinite(v) ? null : v;
  };

  // The coloured areas, for the Mapbox fill under the labels.
  const areas = useMemo(() => (!isArea ? null : {
    type: 'FeatureCollection',
    features: layer.features.map((f, i) => {
      const v = value(i);
      return {
        type: 'Feature',
        geometry: geometryOf(f),
        properties: {
          c: v == null ? 'rgb(140,140,140)' : `rgb(${colorOf(v).join(',')})`,
          o: v == null ? Math.min(NO_DATA_OPACITY, fill) : fill,
        },
      };
    }),
    // value() reads layer and h, both listed.
    // eslint-disable-next-line
  }), [isArea, layer, h, colorOf, fill]);

  const layers = useMemo(() => {
    if (!layer) return [];
    if (level === 'link' && viewLinks) {
      return [new GeoJsonLayer({
        id: 'an-view-links',
        data: viewLinks,
        stroked: true,
        filled: false,
        getLineColor: (f) => {
          if (f.properties.edgeId === selectedId) return PICKED;
          const v = linkValue(f.properties, metric.id);
          return v == null || !Number.isFinite(v) ? NO_DATA : [...colorOf(v), 180];
        },
        getLineWidth: (f) => (f.properties.edgeId === selectedId ? 6 : ROAD_WIDTH[f.properties.highway] || 1.1),
        lineWidthUnits: 'pixels',
        lineCapRounded: true,
        lineJointRounded: true,
        pickable: true,
        autoHighlight: true,
        highlightColor: [255, 255, 255, 160],
        updateTriggers: { getLineColor: [metric.id, colorOf, selectedId], getLineWidth: [selectedId] },
      })];
    }
    if (level === 'link') {
      return [new PathLayer({
        id: 'an-links',
        data: layer.features.map((f, i) => ({ f, i })),
        getPath: (d) => d.f.path,
        getColor: (d) => {
          if (d.i === selected) return PICKED;
          const v = value(d.i);
          return v == null ? NO_DATA : [...colorOf(v), 180];
        },
        getWidth: (d) => (d.i === selected ? 6 : ROAD_WIDTH[d.f.kind] || 1.1),
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        pickable: true,
        autoHighlight: true,
        highlightColor: [255, 255, 255, 160],
        updateTriggers: { getColor: [h, colorOf, selected], getWidth: [selected] },
      })];
    }
    // Invisible areas on top: hover, click, and the outline of the one selected.
    return [new GeoJsonLayer({
      id: `an-${level}-pick`,
      data: layer.features.map((f, i) => ({ type: 'Feature', geometry: geometryOf(f), properties: { i } })),
      filled: true,
      stroked: true,
      getFillColor: INVISIBLE,
      getLineColor: (d) => (d.properties.i === selected ? PICKED : [0, 0, 0, 0]),
      getLineWidth: (d) => (d.properties.i === selected ? 3 : 0),
      lineWidthUnits: 'pixels',
      pickable: true,
      autoHighlight: true,
      highlightColor: [255, 255, 255, 45],
      updateTriggers: { getLineColor: [selected], getLineWidth: [selected] },
    })];
    // eslint-disable-next-line
  }, [layer, level, h, colorOf, selected, viewLinks, selectedId, metric]);

  const isViewLink = (object) => !!object?.properties?.edgeId;
  const indexOf = (object) => {
    if (!object) return null;
    if (isViewLink(object)) return linkIndex?.get(object.properties.edgeId) ?? null;
    return object.i ?? object.properties?.i ?? null;
  };
  const when = metric.hourly === false ? 'per year' : `${String(h).padStart(2, '0')}:00–${String((h + 1) % 24).padStart(2, '0')}:00`;
  const getTooltip = ({ object }) => {
    if (isViewLink(object)) {
      const p = object.properties;
      const name = p.name || `Unnamed ${(p.highway || 'road').replace('_link', ' ramp').replace('_', ' ')}`;
      return `${name}\n${metric.label}: ${fmtValue(linkValue(p, metric.id), metric)} ${metric.unit}\n${when}`;
    }
    const i = indexOf(object);
    if (i == null) return null;
    const f = layer.features[i];
    const v = layer.values(i, h);
    return `${f.name}\n${metric.label}: ${fmtValue(v, metric)} ${metric.unit}\n${when}`;
  };

  return (
    <div className="ws-map" ref={host}>
      <DeckGL
        initialViewState={HEX_VIEW}
        onViewStateChange={onViewChange ? onViewStateChange : undefined}
        controller={true}
        layers={layers}
        pickingRadius={5}
        getTooltip={getTooltip}
        onClick={(info) => onSelect(indexOf(info.object))}
      >
        <Map
          mapStyle="mapbox://styles/mapbox/dark-v10"
          customAttribution='<a href="https://511.org" target="_blank" rel="noopener noreferrer">Bay Area transit: data provided by 511.org</a>'
          // The basemap's first label layer: the areas go just below it.
          onLoad={(e) => setLabelsFrom(e.target.getStyle().layers.find((l) => l.type === 'symbol')?.id || null)}
        >
          {areas && labelsFrom !== undefined && (
            <Source id="an-areas" type="geojson" data={areas}>
              <Layer
                id="an-areas-fill"
                type="fill"
                beforeId={labelsFrom || undefined}
                paint={{ 'fill-color': ['get', 'c'], 'fill-opacity': ['get', 'o'] }}
              />
              <Layer
                id="an-areas-edge"
                type="line"
                beforeId={labelsFrom || undefined}
                paint={{ 'line-color': '#ffffff', 'line-opacity': 0.15, 'line-width': 0.6 }}
              />
            </Source>
          )}
        </Map>
      </DeckGL>
      {children}
      {legend}
    </div>
  );
}
